import * as React from "react";
// tslint:disable-next-line:no-implicit-dependencies
import * as rx from "rxjs";
import {
    Checkbox,
    CheckboxProps,
    SelectInput,
    SelectInputOption,
    SelectInputProps,
    TextInput,
    TextInputProps
} from "@vebox/etui-core";
import {VeboxSelectionList, VeboxSelectionListProps} from "../selection_list/VeboxSelectionList";
import {generic_value} from "../../types/veboxModels";

interface VeboxFormBlocProviderType {
    bloc: FormBloc;
}

const VeboxFormContext = React.createContext<VeboxFormBlocProviderType>({} as any);

interface VeboxFormProps {
    values?: any;
    onChange?: (data: any) => void;
}

export class VeboxForm extends React.Component<VeboxFormProps> {

    public static Checkbox: React.FC<CheckboxProps> = (props) => {

        const [checked, setValue] = VeboxForm.useFormValueState<boolean>(props.name);

        const toggleCheck = (e) => {
            if (props.onChange) {
                props.onChange(e);
            }
            setValue(e.target.checked);
        }

        return (
            <Checkbox {...props} checked={checked} onChange={toggleCheck}/>
        )
    }

    public static TextInput: React.FC<TextInputProps> = (props) => {

        const [value, setValue] = VeboxForm.useFormValueState<string>(props.name);
        const {dangerouslySetInnerHTML, ...rest} = props;
        const changeTextValue = (e) => {
            if (props.onChange) {
                props.onChange(e);
            }
            setValue(e.target.value);
        };

        return (
            <TextInput value={value ?? ""} onChange={changeTextValue} {...rest}/>
        );
    }

    public static SelectInput: React.FC<SelectInputProps> = (props) => {

        const [value, setValue] = VeboxForm.useFormValueState<SelectInputOption>(props.name);

        const [selectKey, setSelectKey] = React.useState<number>(Math.random()); // fixme -> Remove when eTUI SelectInput has an empty option

        React.useEffect(() => {
            if (!value) {
                setSelectKey(Math.random());
            }
        }, [value]);

        const changeSelectOption = (newValue, actionMeta) => {
            if (props.onChange) {
                props.onChange(newValue, actionMeta);
            }

            setValue(newValue);
        };

        return (
            <SelectInput
                key={selectKey}
                {...props}
                value={value}
                onChange={changeSelectOption}
            />
        );
    }

    public static SelectionList: React.FC<{
        name: string
    } & VeboxSelectionListProps & React.HTMLAttributes<any>> = (props) => {

        const [value, setValue] = VeboxForm.useFormValueState<generic_value[]>(props.name);

        const updateValues = (values) => {
            if (props.onChange) {
                props.onChange(values);
            }
            setValue(values);
        }

        return (
            <VeboxSelectionList
                {...props}
                values={value}
                onChange={updateValues}
            >
                {props.children}
            </VeboxSelectionList>
        )
    }

    public static Section: React.FC<{ name: string } & VeboxFormProps> = (props) => {

        const [value, setValue] = VeboxForm.useFormValueState<any>(props.name);

        const changeValues = (newValues) => {
            setValue(newValues);
        };

        return (
            <FormBlocProvider value={value} onChange={changeValues}>
                {props.children}
            </FormBlocProvider>
        );
    }

    public static useFormData = () => {
        return React.useContext(VeboxFormContext);
    }

    public static useFormValueState<T>(name: string): [T, (v: T) => void] {
        const {bloc} = React.useContext(VeboxFormContext);

        const [value, setValue] = React.useState<T>();

        React.useEffect(() => {
            const subscription = bloc.get<T>(name, (newValue) => {
                setValue(newValue);
            });
            return () => subscription.unsubscribe();
        }, []);

        return [value, (newValue) => bloc.set<T>(name, newValue)]
    }

    public render() {
        return (
            <FormBlocProvider value={this.props.values} onChange={this.props.onChange}>
                {this.props.children}
            </FormBlocProvider>
        );
    }
}

class FormBloc { // ToDo: Handle external request of value change with setAll

    private valuesSubjects: Map<string, rx.Subject<any>>;
    private values: any;

    constructor(private onChange?: (values: any) => void) {
        this.valuesSubjects = new Map<string, rx.Subject<any>>();
        this.values = {};
    }

    public setAll(newValues: any) {
        // Compare deep compare old values with new values
        // Emit changes to field that are different
        if (!newValues) {
            return;
        }

        Object.keys({...this.values, ...newValues}).forEach(key => {
            if (JSON.stringify(this.values[key]) !== JSON.stringify(newValues[key])) {
                const value = newValues[key];
                const name = key;
                this.createSubjectIfNotExist(name);
                this.valuesSubjects.get(name).next(value);
            }
        });
        this.values = newValues;
        if (this.onChange) {
            this.onChange(this.values);
        }
    }

    public set<T = any>(name: string, value: T) {
        this.createSubjectIfNotExist<T>(name);
        this.valuesSubjects.get(name).next(value);
        this.values = {
            ...this.values,
            [name]: value,
        };
        if (this.onChange) {
            this.onChange(this.values);
        }
    }

    public get<T = any>(name: string, listener: (value: T) => void) {
        this.createSubjectIfNotExist(name);
        return this.valuesSubjects.get(name).subscribe(listener);
    }

    private createSubjectIfNotExist<T>(name: string) {
        if (!this.valuesSubjects.has(name)) {
            this.valuesSubjects.set(name, new rx.Subject<T>())
        }
    }
}

const FormBlocProvider: React.FC<{ value?: any, onChange?: (values: any) => void }> = (props) => {

    const [bloc] = React.useState<FormBloc>(() => new FormBloc(props.onChange));

    React.useEffect(() => {
        bloc.setAll(props.value);
    }, [props.value]);

    return (
        <VeboxFormContext.Provider value={{bloc}}>
            {props.children}
        </VeboxFormContext.Provider>
    );
}